import { AdditionalServicesService } from '@additional-services/services/additional-services.service';
import { GaEventService } from '@analytics/services/ga.event.service';
import { Location } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CompleteOrderService } from '@complete-order/services/complete-order.service';
import {
  ErrorType,
  OfferType,
  Offers,
  ReplacementOffer,
  Specification,
  fadeInOut,
  slideInOut,
} from '@dgx-pr-spa/shared/util';
import { BuildConfigService } from '@dgx-pr-spa/ui-components';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ProductDetailsService } from '@product-details/services/product-details.service';
import { EarliestDeliveryDateTextPipe } from '@shared/pipes/earliest-delivery-date-text.pipe';
import { UrlService } from '@shared/services/url.service';
import { ViewOffersService } from '@view-offers/services/view-offers.service';
@UntilDestroy()
@Component({
  selector: 'app-product-details-container',
  templateUrl: './product-details-container.component.html',
  styleUrls: ['./product-details-container.component.scss'],
  animations: [fadeInOut, slideInOut],
  providers: [EarliestDeliveryDateTextPipe],
})
export class ProductDetailsContainerComponent implements OnInit, AfterViewInit {
  constructor(
    public readonly viewOffersService: ViewOffersService,
    public readonly router: Router,
    public readonly route: ActivatedRoute,
    public readonly completeOrderService: CompleteOrderService,
    public readonly productDetailsService: ProductDetailsService,
    public readonly additionalServicesService: AdditionalServicesService,
    public readonly gaEventService: GaEventService,
    public readonly configService: BuildConfigService,
    private location: Location,
    private urlService: UrlService
  ) {}

  /**
   * Set the limit on the number of offers that can be compared
   */
  @Input() limit = 2;

  /**
   * Record the number of comparisons selected
   */
  @Input() comparisonsSelected = [] as ReplacementOffer[];

  /**
   * Receives complete offers data
   */
  @Input() offers: Offers;

  previousUrl: string | null;

  private _selected;
  private _comparisonsSelected;

  get selected(): ReplacementOffer {
    return this._selected;
  }

  /**
   * Receives the selected offer
   */
  @Input()
  set selected(selected: ReplacementOffer) {
    this._selected = selected;
    if (this.selected && this.offers) {
      this.compareSpec =
        this.viewOffersService.isSelectedSpecsDifferentToCurrent(
          this.offers.current_item,
          this.selected
        );
    }
  }

  /**
   * Receives the offer reference
   */
  @Input() offerReference: string;

  /**
   * Store observable of the is delivery available state
   */
  isDeliveryServiceAvailable$ =
    this.additionalServicesService.isDeliveryAvailable$;

  /**
   * Observable of the offers with the current selected offer filtered out
   */
  filteredAnotherLookOffers$ = this.viewOffersService.selectedFilteredOffers$;

  /**
   * Observable boolean of delivery selected state
   */
  isDeliverySelected$ = this.additionalServicesService.isDeliverySelected$;

  /**
   * Observable of the delivery service data on the selected item
   */
  getDelivery$ = this.additionalServicesService.getDelivery$;

  /**
   * Observable of the delivery dates available
   */
  deliveryDates$ = this.productDetailsService.deliveryDates$;

  /**
   * Observable of the comparisons selected
   */
  comparisons$ = this.viewOffersService.comparisons$;

  @ViewChild('compareModelsButton') compareModelsButton: ElementRef;

  /**
   * Observable of delivery dates api error
   */
  isDeliveryDatesApiError$ = this.productDetailsService.deliveryApiError$;

  /**
   * Check offer upsell is toggle on
   */
  upsellEnabled$ = this.additionalServicesService.upsellEnabled$;

  /**
   * enum of offer types
   */
  offerType = OfferType;

  /**
   * enum of error types
   */
  errorType = ErrorType;

  /**
   * Check if selected offer has spec difference
   */
  compareSpec: {
    hasDifference: boolean;
    current: Specification[];
    selected: Specification[];
  };

  ngOnInit() {
    this.urlService.previousUrl$
      .pipe(untilDestroyed(this))
      .subscribe((previousUrl: string) => {
        this.previousUrl = previousUrl;
      });
  }

  /**
   * Scrolls to top of page after view init
   */
  ngAfterViewInit(): void {
    this._comparisonsSelected = this.comparisonsSelected;
    this.viewOffersService.scrollIntoView('header', 'smooth');
    this.viewOffersService.clearComparisons();
  }

  /**
   * Selects offer and scrolls to top of page
   */
  onCardClick(offer: ReplacementOffer) {
    this.viewOffersService.scrollIntoView('header', 'smooth');
    this.viewOffersService.selectOffer(offer);
  }

  /**
   * Parse appliance title
   */
  getSpecificationTitle(selected: ReplacementOffer) {
    const long = selected.long_description;
    const start = selected.short_description?.length || 0;

    return long.substr(start).trim();
  }

  /**
   * Offer Direct card compare CTA click
   */
  onDirectComparisonClick(comparison: ReplacementOffer) {
    this.viewOffersService.setDirectComparisons(comparison);
  }

  /**
   * Offer card compare CTA click
   */
  onComparisonClick(comparison: ReplacementOffer) {
    this.viewOffersService.setComparisons(comparison);
  }

  onCompareClicked() {
    this.router.navigate(['/model-comparison'], {
      queryParamsHandling: 'preserve',
    });
    this.gaEventService.OffersCompareNowClick();
  }

  /**
   * Comparison selected from compare basket
   */
  onComparisonSelectedClick(offer: ReplacementOffer) {
    this.viewOffersService.scrollIntoView('header');
    this.viewOffersService.selectOffer(offer);
  }

  navSpecHeaderClick() {
    this.viewOffersService.scrollIntoView('specHeader', 'smooth');
  }

  /**
   * Continue click
   */
  continueClick() {
    this.gaEventService.ProductDetailsContinueClick();
    this.router.navigate(['/services'], {
      queryParams: { PRRef: this.offerReference },
    });
  }

  /**
   * Carousel thumbnail click
   */
  onThumbnailClicked() {
    this.gaEventService.ProductDetailsThumbnailClick();
  }

  /**
   * Getter returns boolean to set the disabled state of the all non selected CTAs
   */
  get limitReached() {
    return !!(this.comparisonsSelected?.length === this.limit);
  }

  routeBack() {
    const singleOption = this.offers.replacement_offers?.length === 1;
    this.router.navigate([singleOption ? '/tips' : '/offers'], {
      queryParamsHandling: 'preserve',
    });
  }
}
